import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Droplets, Fuel, Gauge, Route, X } from "lucide-react";
import { getVehicleFuel, listFuelEvents } from "../api";
import type { FuelEvent, FuelTankState, Vehicle } from "../types";

export function FuelPanel({
  open,
  onClose,
  vehicle,
}: {
  open: boolean;
  onClose: () => void;
  vehicle: Vehicle;
}) {
  const [tanks, setTanks] = useState<FuelTankState[]>([]);
  const [events, setEvents] = useState<FuelEvent[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const refresh = () => {
      setError("");
      Promise.all([getVehicleFuel(vehicle.id), listFuelEvents(vehicle.id)])
        .then(([tankRows, eventRows]) => {
          setTanks(tankRows);
          setEvents(eventRows);
        })
        .catch((reason) => setError(reason instanceof Error ? reason.message : "No fue posible cargar combustible"));
    };
    refresh();
    window.addEventListener("gsf:fuel-updated", refresh);
    return () => window.removeEventListener("gsf:fuel-updated", refresh);
  }, [open, vehicle.id]);

  const totals = useMemo(
    () => ({
      liters: tanks.reduce((sum, tank) => sum + (tank.estimated_liters || 0), 0),
      capacity: tanks.reduce((sum, tank) => sum + tank.capacity_liters, 0),
      range: tanks.reduce((sum, tank) => sum + (tank.estimated_range_km || 0), 0),
    }),
    [tanks],
  );

  if (!open) return null;
  return (
    <div className="admin-overlay" role="presentation" onMouseDown={onClose}>
      <aside
        className="admin-panel fuel-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="fuel-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <span className="eyebrow">CONTROL DE COMBUSTIBLE</span>
            <h2 id="fuel-title">{vehicle.unit_code} · Tanques y consumo</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar combustible"><X size={18} /></button>
        </header>
        <section className="admin-panel__summary">
          <span><Fuel size={17} /><b>{Math.round(totals.liters)}</b> litros</span>
          <span><Gauge size={17} /><b>{Math.round(totals.capacity)}</b> capacidad</span>
          <span><Route size={17} /><b>{Math.round(totals.range)}</b> km estimados</span>
        </section>
        <div className="fuel-panel__body">
          <section className="fuel-tanks">
            {tanks.map((tank) => (
              <article key={tank.tank_id}>
                <div className="fuel-gauge">
                  <span style={{ height: `${tank.fuel_percent || 0}%` }} />
                  <b>{tank.fuel_percent ?? "—"}%</b>
                </div>
                <div>
                  <small>{tank.tank_code.toUpperCase()} · {tank.sensor_type}</small>
                  <strong>{tank.estimated_liters ?? "—"} L disponibles</strong>
                  <em>{tank.capacity_liters} L capacidad · {tank.estimated_range_km ?? "—"} km autonomía</em>
                </div>
              </article>
            ))}
            {!tanks.length && <p className="maintenance-empty">SIN TANQUES CONFIGURADOS</p>}
          </section>
          <section className="fuel-events">
            <span className="eyebrow">EVENTOS CORRELACIONADOS</span>
            {events.map((event) => (
              <article className={`fuel-event fuel-event--${event.event_type}`} key={event.id}>
                {event.event_type === "suspected_theft" ? <AlertTriangle size={17} /> : <Droplets size={17} />}
                <span>
                  <small>{new Date(event.captured_at).toLocaleString("es-MX")}</small>
                  <strong>{event.event_type === "suspected_theft" ? "POSIBLE EXTRACCIÓN" : "CARGA DETECTADA"}</strong>
                  <em>{event.delta_percent > 0 ? "+" : ""}{event.delta_percent}% · {event.estimated_liters} L</em>
                </span>
              </article>
            ))}
            {!events.length && <p className="maintenance-empty">SIN VARIACIONES ANÓMALAS</p>}
          </section>
        </div>
        {error && <div className="admin-message admin-message--error">{error}</div>}
      </aside>
    </div>
  );
}
